import { useActiveAlerts } from '../../../../../hooks/useDataQuality'
import { Alert } from '../../../../../types/dataQuality'
import LoadingSkeleton from '../../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../../common/ErrorDisplay'
import './ActiveAlerts.css'

interface ActiveAlertsProps {
  limit?: number
}

const ActiveAlerts = ({ limit = 3 }: ActiveAlertsProps) => {
  const { data, isLoading, error } = useActiveAlerts({ limit })

  if (isLoading) {
    return (
      <div className="active-alerts-section">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Active Alerts</h3>
        </div>
        <LoadingSkeleton variant="card" count={3} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="active-alerts-section">
        <ErrorDisplay
          error={error.message}
          onRetry={() => window.location.reload()}
          title="Failed to Load Alerts"
        />
      </div>
    )
  }

  if (!data || !data.alerts || data.alerts.length === 0) {
    return (
      <div className="active-alerts-section">
        <div className="section-header-compact">
          <h3 className="section-title-compact">Active Alerts</h3>
          <p className="section-subtitle-compact">0 alerts requiring attention</p>
        </div>
        <div className="no-alerts-message">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#00C950" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <p>No active alerts at this time</p>
        </div>
      </div>
    )
  }

  const getSeverityColor = (severity: string) => {
    switch (severity.toUpperCase()) {
      case 'CRITICAL':
        return 'critical'
      case 'HIGH':
        return 'high'
      case 'MEDIUM':
        return 'medium'
      case 'LOW':
        return 'low'
      default:
        return 'medium'
    }
  }

  const getSeverityIcon = (severity: string) => {
    const severityUpper = severity.toUpperCase()
    if (severityUpper === 'CRITICAL' || severityUpper === 'HIGH') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#FB2C36" strokeWidth="2" fill="none"/>
          <line x1="12" y1="7" x2="12" y2="13" stroke="#FB2C36" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="12" cy="16" r="1.5" fill="#FB2C36"/>
        </svg>
      )
    }
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="#F0B100" strokeWidth="2" fill="none"/>
        <line x1="12" y1="7" x2="12" y2="13" stroke="#F0B100" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="12" cy="16" r="1.5" fill="#F0B100"/>
      </svg>
    )
  }

  const formatCategoryName = (metricName: string | undefined, alertType: string) => {
    if (!metricName && !alertType) return 'General'
    
    const name = (metricName || alertType).toLowerCase()
    
    // Map to KPI indicator names
    if (name.includes('trust') || name.includes('trust_score')) return 'Data Trust Score'
    if (name.includes('complete')) return 'Completeness'
    if (name.includes('fresh') || name.includes('timely') || name.includes('timeliness')) return 'Timeliness'
    if (name.includes('quality') && name.includes('rule')) return 'Quality Rules Pass'
    if (name.includes('schema') && name.includes('stab')) return 'Schema Stability'
    if (name.includes('pipeline')) return 'Pipeline Success'
    if (name.includes('sla')) return 'SLA Compliance'
    if (name.includes('error')) return 'Data Quality'
    if (name.includes('duplicate')) return 'Data Quality'
    if (name.includes('missing')) return 'Data Quality'
    if (name.includes('format')) return 'Data Quality'
    
    // Default: capitalize first letter of each word
    return (metricName || alertType)
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  return (
    <div className="active-alerts-section">
      <div className="section-header-compact">
        <h3 className="section-title-compact">Active Alerts</h3>
        <p className="section-subtitle-compact">
          {data.summary.active_alerts} alert{data.summary.active_alerts !== 1 ? 's' : ''} requiring attention
        </p>
      </div>

      <div className="alerts-list">
        {data.alerts.slice(0, limit).map((alert: Alert) => (
          <div key={alert.id} className={`alert-item alert-${getSeverityColor(alert.severity)}`}>
            <div className="alert-icon-wrapper">
              {getSeverityIcon(alert.severity)}
            </div>

            <div className="alert-content">
              <div className="alert-title-row">
                <span className="alert-title">{alert.alert_title}</span>
                <span className={`alert-severity ${getSeverityColor(alert.severity)}`}>
                  {alert.severity}
                </span>
              </div>
              <p className="alert-source">
                Source: {alert.related_metrics?.source_name || 'Unknown Source'}
              </p>
            </div>

            <div className="alert-category-wrapper">
              <span className="alert-category">
                {formatCategoryName(alert.related_metrics?.metric_name, alert.alert_type)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {data.summary.critical_alerts > 0 && (
        <div className="alert-summary-footer">
          <span className="alert-summary-item critical">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FB2C36" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            {data.summary.critical_alerts} Critical
          </span>
          {data.summary.high_alerts > 0 && (
            <span className="alert-summary-item high">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#F0B100">
                <circle cx="12" cy="12" r="10"/>
              </svg>
              {data.summary.high_alerts} High
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default ActiveAlerts
